<template>
  <div class="fixed inset-0 z-50 overflow-y-auto">
    <div class="flex items-center justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
      <!-- Background overlay -->
      <div
        class="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity"
        @click="$emit('close')" />

      <!-- Modal panel -->
      <div
        class="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl
               transform transition-all sm:my-8 sm:align-middle sm:max-w-2xl sm:w-full">
        <!-- Header -->
        <div class="bg-gradient-to-r from-indigo-500 to-indigo-600 px-4 py-5 sm:px-6">
          <div class="flex items-center justify-between">
            <div class="flex items-center">
              <div class="flex-shrink-0">
                <div class="h-10 w-10 rounded-full bg-white bg-opacity-20 flex items-center justify-center">
                  <svg
                    class="h-6 w-6 text-white"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7
                         20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288
                         0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                  </svg>
                </div>
              </div>
              <h3 class="ml-3 text-lg leading-6 font-semibold text-white">
                Select {{ instanceType === 'lead' ? 'Lead' : 'Support' }} Contact
              </h3>
            </div>
            <button
              class="rounded-md text-white hover:text-gray-200 focus:outline-none focus:ring-2 focus:ring-white"
              @click="$emit('close')">
              <span class="sr-only">Close</span>
              <svg
                class="h-6 w-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24">
                <path
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  stroke-width="2"
                  d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>
        </div>

        <div class="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
          <div class="w-full">
            <!-- Search -->
            <div class="mb-6 relative">
              <div class="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <svg
                  class="h-5 w-5 text-gray-400"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                </svg>
              </div>
              <input
                v-model="searchQuery"
                type="text"
                placeholder="Search by name, email, phone, or company..."
                class="pl-10 pr-3 py-2 shadow-sm focus:ring-indigo-500 focus:border-indigo-500
                       block w-full sm:text-sm border-gray-300 rounded-lg"
                @input="debouncedSearch">
              <div
                v-if="searchQuery"
                class="absolute inset-y-0 right-0 pr-3 flex items-center">
                <button
                  class="text-gray-400 hover:text-gray-500"
                  @click="searchQuery = ''">
                  <svg
                    class="h-5 w-5"
                    fill="currentColor"
                    viewBox="0 0 20 20">
                    <path
                      fill-rule="evenodd"
                      d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293
                         1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1
                         1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                      clip-rule="evenodd" />
                  </svg>
                </button>
              </div>
            </div>

            <!-- Results count -->
            <div
              v-if="!loading && contacts.length > 0"
              class="mb-3 text-sm text-gray-600">
              Found <span class="font-semibold">{{ totalCount || contacts.length }}</span> {{ instanceType }} contacts
              <span
                v-if="searchQuery"
                class="text-gray-500">
                matching "{{ searchQuery }}"
              </span>
            </div>

            <!-- Contacts List -->
            <div
              v-if="loading"
              class="flex justify-center py-8">
              <div class="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600" />
            </div>

            <div
              v-else-if="contacts.length === 0"
              class="text-center py-8 text-gray-500">
              <div
                v-if="hasSearchQuery"
                class="mb-4">
                <p class="text-gray-600">
                  No contacts found matching "{{ searchQuery }}"
                </p>
                <button
                  class="mt-2 text-sm text-indigo-600 hover:text-indigo-500"
                  @click="searchQuery = ''">
                  Clear search
                </button>
              </div>
              <div v-else>
                <svg
                  class="mx-auto h-12 w-12 text-gray-400 mb-4"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24">
                  <path
                    stroke-linecap="round"
                    stroke-linejoin="round"
                    stroke-width="2"
                    d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7
                       20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288
                       0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
                </svg>
                <p class="text-lg font-medium text-gray-900 mb-2">
                  No GoHighLevel {{ instanceType }} contacts found
                </p>
                <p class="text-sm text-gray-600 mb-4">
                  No {{ instanceType }} contacts have been synced from GoHighLevel yet.
                </p>
                <button
                  class="inline-flex items-center px-3 py-2 border border-transparent shadow-sm text-sm leading-4
                         font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 focus:outline-none
                         focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                  @click="syncContacts">
                  <svg
                    class="mr-2 -ml-0.5 h-4 w-4"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24">
                    <path
                      stroke-linecap="round"
                      stroke-linejoin="round"
                      stroke-width="2"
                      d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  Sync Contacts from GoHighLevel
                </button>
                <p class="text-xs text-gray-500 mt-2">
                  Make sure GoHighLevel is configured for this organization first
                </p>
              </div>
            </div>

            <div
              v-else
              class="max-h-96 overflow-y-auto border border-gray-200 rounded-lg">
              <div class="divide-y divide-gray-200">
                <div
                  v-for="contact in contacts"
                  :key="contact.id"
                  class="group relative px-4 py-4 hover:bg-gray-50 cursor-pointer transition-colors
                         duration-150 ease-in-out"
                  @click="selectContact(contact)">
                  <div class="flex items-start space-x-3">
                    <!-- Avatar/Initial -->
                    <div class="flex-shrink-0">
                      <div class="h-10 w-10 rounded-full bg-indigo-100 flex items-center justify-center">
                        <span class="text-sm font-medium text-indigo-700">
                          {{ getInitials(contact.display_name) }}
                        </span>
                      </div>
                    </div>

                    <!-- Contact Info -->
                    <div class="flex-1 min-w-0">
                      <div class="flex items-center justify-between">
                        <p class="text-sm font-semibold text-gray-900 truncate">
                          {{ contact.display_name || 'Unknown Contact' }}
                        </p>
                        <!-- Select Arrow -->
                        <svg
                          class="ml-2 h-5 w-5 text-gray-400 group-hover:text-gray-600 flex-shrink-0"
                          fill="none"
                          stroke="currentColor"
                          viewBox="0 0 24 24">
                          <path
                            stroke-linecap="round"
                            stroke-linejoin="round"
                            stroke-width="2"
                            d="M9 5l7 7-7 7" />
                        </svg>
                      </div>

                      <div class="mt-1 flex items-center space-x-4 text-xs text-gray-500">
                        <span
                          v-if="contact.email"
                          class="flex items-center">
                          <svg
                            class="mr-1 h-3 w-3"
                            fill="currentColor"
                            viewBox="0 0 20 20">
                            <path d="M2.003 5.884L10 9.882l7.997-3.998A2 2 0 0016 4H4a2 2 0 00-1.997 1.884z" />
                            <path d="M18 8.118l-8 4-8-4V14a2 2 0 002 2h12a2 2 0 002-2V8.118z" />
                          </svg>
                          {{ contact.email }}
                        </span>
                        <span
                          v-if="contact.phone"
                          class="flex items-center">
                          <svg
                            class="mr-1 h-3 w-3"
                            fill="currentColor"
                            viewBox="0 0 20 20">
                            <path
                              d="M2 3a1 1 0 011-1h2.153a1 1 0 01.986.836l.74 4.435a1 1 0 01-.54 1.06l-1.548
                                   .773a11.037 11.037 0 006.105 6.105l.774-1.548a1 1 0 011.059-.54l4.435.74a1 1 0
                                   01.836.986V17a1 1 0 01-1 1h-2C7.82 18 2 12.18 2 5V3z" />
                          </svg>
                          {{ formatPhone(contact.phone) }}
                        </span>
                      </div>

                      <div
                        v-if="contact.company_name"
                        class="mt-1 text-xs text-gray-500">
                        <span class="flex items-center">
                          <svg
                            class="mr-1 h-3 w-3"
                            fill="currentColor"
                            viewBox="0 0 20 20">
                            <path
                              fill-rule="evenodd"
                              d="M4 4a2 2 0 012-2h8a2 2 0 012 2v12a1 1 0 110 2h-3a1 1 0 01-1-1v-2a1 1 0 00-1-1H9a1
                                 1 0 00-1 1v2a1 1 0 01-1 1H4a1 1 0 110-2V4zm3 1h2v2H7V5zm2 4H7v2h2V9zm2-4h2v2h-2V5zm2
                                 4h-2v2h2V9z"
                              clip-rule="evenodd" />
                          </svg>
                          {{ contact.company_name }}
                        </span>
                      </div>

                      <!-- Tags -->
                      <div
                        v-if="contact.tags && contact.tags.length > 0"
                        class="mt-2 flex flex-wrap gap-1">
                        <span
                          v-for="tag in contact.tags.slice(0, 3)"
                          :key="tag"
                          class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium
                                 bg-indigo-100 text-indigo-700">
                          {{ tag }}
                        </span>
                        <span
                          v-if="contact.tags.length > 3"
                          class="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium
                                 bg-gray-100 text-gray-600">
                          +{{ contact.tags.length - 3 }} more
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <!-- Pagination -->
            <div
              v-if="totalPages > 1"
              class="mt-4 flex justify-between items-center">
              <button
                :disabled="currentPage === 1"
                class="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium
                       rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2
                       focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                @click="currentPage--">
                Previous
              </button>
              <span class="text-sm text-gray-700">
                Page {{ currentPage }} of {{ totalPages }}
              </span>
              <button
                :disabled="currentPage === totalPages"
                class="inline-flex items-center px-3 py-2 border border-gray-300 text-sm leading-4 font-medium
                       rounded-md text-gray-700 bg-white hover:bg-gray-50 focus:outline-none focus:ring-2
                       focus:ring-offset-2 focus:ring-indigo-500 disabled:opacity-50 disabled:cursor-not-allowed"
                @click="currentPage++">
                Next
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import {
  ref, watch, onMounted, computed
} from 'vue'

// Simple debounce implementation
function debounce(func, wait) {
  let timeout
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout)
      func(...args)
    }
    clearTimeout(timeout)
    timeout = setTimeout(later, wait)
  }
}

const props = defineProps({
  organizationId: {
    type: String,
    required: true
  },
  instanceType: {
    type: String,
    required: true,
    validator: (value) => ['lead', 'support'].includes(value)
  }
})

const emit = defineEmits(['select', 'close'])

const loading = ref(false)
const contacts = ref([])
const searchQuery = ref('')
const currentPage = ref(1)
const totalPages = ref(1)
const totalCount = ref(0)
const perPage = 20

const hasSearchQuery = computed(() => searchQuery.value.trim().length > 0)

const fetchContacts = async() => {
  try {
    loading.value = true
    const params = new URLSearchParams({
      instance_type: props.instanceType,
      page: currentPage.value,
      per_page: perPage
    })

    if (searchQuery.value) {
      params.append('q', searchQuery.value)
    }

    const url = `/ajax/ghl/contacts?${params}`
    // Log fetch details for debugging

    const response = await fetch(url, {
      headers: {
        Accept: 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
      }
    })

    // Check response status

    if (!response.ok) {
      await response.text()
      // Log error for debugging
      throw new Error(`HTTP error! status: ${response.status}`)
    }

    const data = await response.json()
    // Process response data

    contacts.value = data.contacts || []
    totalPages.value = data.total_pages || 1
    totalCount.value = data.total_count || contacts.value.length
    // Successfully loaded contacts
  } catch (error) {
    // Handle fetch error
    contacts.value = []
    totalPages.value = 1
    // Notify user of error
  } finally {
    loading.value = false
  }
}

const debouncedSearch = debounce(() => {
  currentPage.value = 1
  fetchContacts()
}, 300)

const selectContact = (contact) => {
  emit('select', contact)
}

const syncContacts = async() => {
  try {
    loading.value = true
    const response = await fetch('/ajax/ghl/sync', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        'X-CSRF-Token': document.querySelector('meta[name="csrf-token"]')?.content || ''
      },
      body: JSON.stringify({
        sync_type: 'contacts'
      })
    })

    if (response.ok) {
      // Notify user sync started
      // Refresh contacts after a delay
      setTimeout(() => fetchContacts(), 5000)
    } else {
      const errorData = await response.json()
      throw new Error(errorData.error || `HTTP error! status: ${response.status}`)
    }
  } catch (error) {
    // Handle sync error
  } finally {
    loading.value = false
  }
}

watch(currentPage, () => {
  fetchContacts()
})

onMounted(() => {
  // Component mounted, fetch initial data
  fetchContacts()
})

// Helper functions
const getInitials = (name) => {
  if (!name || name.trim() === '') return '?'
  const parts = name.trim().split(' ')
  if (parts.length >= 2) {
    return (parts[0][0] + parts[parts.length - 1][0]).toUpperCase()
  }
  return name.substring(0, 2).toUpperCase()
}

const formatPhone = (phone) => {
  if (!phone) return ''
  // Remove all non-numeric characters
  const cleaned = phone.replace(/\D/g, '')
  // Format as US phone number if it's 10 or 11 digits
  if (cleaned.length === 10) {
    return `(${cleaned.substring(0, 3)}) ${cleaned.substring(3, 6)}-${cleaned.substring(6)}`
  } if (cleaned.length === 11 && cleaned[0] === '1') {
    return `+1 (${cleaned.substring(1, 4)}) ${cleaned.substring(4, 7)}-${cleaned.substring(7)}`
  }
  // Return original if not a standard US number
  return phone
}
</script>
